import {
  parseSensorTypeIdsFromDto,
  pickPrimaryBinding,
  stationRoutingKeys,
} from '@/features/realtime/utils/stationSensorBindings';

export interface StationTableRow {
  id: string;
  code: string;
  name: string;
  location: string;
  basinId: string;
  basinName?: string;
  provinceId?: string;
  provinceName?: string;
  wardId?: string;
  wardName?: string;
  lat: number;
  lon: number;
  /** Slug/routing_key chính (ưu tiên water_level → rainfall → …). */
  sensorType: string;
  /** Mọi routing_key gắn trên trạm. */
  sensorTypes: string[];
  /** UUID kênh catalog từ `sensor_type_ids[].id`. */
  sensorChannelIds: string[];
  typeColors?: {
    color?: string;
    color_dark?: string;
    contrast_label?: string;
  };
  isActive?: boolean;
  metadata: Record<string, unknown>;
  raw: Record<string, unknown>;
}

function num(v: unknown): number | undefined {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

/**
 * Parse GeoJSON Point from API `location` (JSON string or object).
 * Per GeoJSON, `coordinates` are [longitude, latitude].
 */
function pointFromLocationField(location: unknown): { lat: number; lon: number } | undefined {
  if (location == null || location === '') return undefined;
  let obj: unknown = location;
  if (typeof location === 'string') {
    const t = location.trim();
    if (!t) return undefined;
    try {
      obj = JSON.parse(t) as unknown;
    } catch {
      return undefined;
    }
  }
  if (!obj || typeof obj !== 'object') return undefined;
  const o = obj as Record<string, unknown>;
  if (o.type !== 'Point') return undefined;
  const c = o.coordinates;
  if (!Array.isArray(c) || c.length < 2) return undefined;
  const lon = Number(c[0]);
  const lat = Number(c[1]);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return undefined;
  return { lat, lon };
}

/** Normalize GET /api/stations DTO for the table (lat/lon, metadata, or GeoJSON in `location`). */
export function mapStationDtoToRow(raw: Record<string, unknown>): StationTableRow {
  const id = String(raw.id ?? raw.code ?? '');
  const meta =
    raw.metadata && typeof raw.metadata === 'object' && !Array.isArray(raw.metadata)
      ? (raw.metadata as Record<string, unknown>)
      : {};
  const fromLoc = pointFromLocationField(raw.location);
  const rawLat = num(raw.lat) ?? num(meta.lat);
  const rawLon = num(raw.lon) ?? num(meta.lon) ?? num(meta.lng);
  const preferLocation =
    fromLoc != null && (rawLat == null || rawLon == null || (rawLat === 0 && rawLon === 0));
  const lat = preferLocation ? fromLoc.lat : (rawLat ?? fromLoc?.lat ?? 0);
  const lon = preferLocation ? fromLoc.lon : (rawLon ?? fromLoc?.lon ?? 0);
  const bindings = parseSensorTypeIdsFromDto(raw);
  const primary = pickPrimaryBinding(bindings);
  const sensorTypes = stationRoutingKeys(bindings);
  const stRoot = raw as { sensor_type_id?: unknown; sensor_type?: unknown };
  const legacyType = String(
    stRoot.sensor_type_id ?? stRoot.sensor_type ?? meta.sensor_type_id ?? meta.sensor_type ?? '',
  ).trim();
  const sensorType = primary?.sensor_type?.trim() || legacyType;
  const sensorChannelIds = bindings.map((b) => b.id);

  return {
    id,
    code: String(raw.code ?? ''),
    name: String(raw.name ?? ''),
    location: String(raw.location ?? ''),
    basinId: String(raw.basin_id ?? ''),
    basinName: typeof raw.basin_name === 'string' ? raw.basin_name : undefined,
    provinceId: typeof raw.province_id === 'string' ? raw.province_id : undefined,
    provinceName: typeof raw.province_name === 'string' ? raw.province_name : undefined,
    wardId: typeof raw.ward_id === 'string' ? raw.ward_id : undefined,
    wardName: typeof raw.ward_name === 'string' ? raw.ward_name : undefined,
    lat,
    lon,
    sensorType,
    sensorTypes: sensorTypes.length > 0 ? sensorTypes : sensorType ? [sensorType] : [],
    sensorChannelIds,
    typeColors:
      typeof raw.type_colors === 'object' && raw.type_colors
        ? (raw.type_colors as { color?: string; color_dark?: string; contrast_label?: string })
        : undefined,
    isActive: typeof raw.isActive === 'boolean' ? raw.isActive : undefined,
    metadata: meta,
    raw,
  };
}
